import { memo } from 'react'
import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import {
  ArrowTopRightOnSquareIcon,
  ChartBarIcon,
  DocumentDuplicateIcon,
  GlobeAltIcon,
  ShareIcon,
} from '@heroicons/react/24/outline'
import { truncateMiddle } from '../../../../shared/lib/analytics-format'
import type { DashboardUrl } from '../../../../shared/types/dashboard'
import { useTranslation } from 'react-i18next'
import { formatDate } from '../../../../shared/lib/format'

interface RecentUrlsTableProps {
  urls: DashboardUrl[]
  onCopySuccess: (text: string) => void
}

const URL_PREVIEW_LENGTH = 56
const COLUMN_KEYS = ['columnLink', 'columnClicks', 'columnCreated', ''] as const

/** 최근 만든 링크 목록 */
function RecentUrlsTable({ urls, onCopySuccess }: RecentUrlsTableProps) {
  const { t } = useTranslation()
  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text)
      onCopySuccess(text)
    } catch (error) {
      console.error('클립보드 복사 실패:', error)
    }
  }

  const share = async (url: DashboardUrl) => {
    if (!navigator.share) {
      copyToClipboard(url.shortUrl)
      return
    }
    try {
      await navigator.share({
        title: url.title || url.metadata?.title || url.shortCode,
        url: url.shortUrl,
      })
    } catch (error) {
      console.error('공유 실패:', error)
    }
  }

  // 막대 길이의 기준. 목록 안에서 가장 많이 눌린 링크를 100% 로 둔다
  const maxClicks = urls.reduce((max, url) => Math.max(max, url.clickCount), 0)

  return (
    <section className="rounded-2xl border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800">
      <header className="flex items-center justify-between px-6 py-4">
        <h3 className="text-base font-bold text-gray-900 dark:text-white">
          {t('dashboard.recentLinks')}
        </h3>
        <Link
          to="/bookmarks"
          className="flex items-center gap-1 text-sm font-semibold text-primary-600 transition hover:text-primary-700 dark:text-primary-400"
        >
          {t('dashboard.viewAll')}
        </Link>
      </header>

      {urls.length === 0 ? (
        <div className="px-6 py-12 text-center">
          <GlobeAltIcon className="mx-auto mb-4 h-12 w-12 text-gray-400" />
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {t('dashboard.noLinks')}
          </p>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            {t('dashboard.noLinksHint')}
          </p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="border-y border-gray-100 bg-gray-50 dark:border-gray-700 dark:bg-gray-900/50">
              <tr>
                {COLUMN_KEYS.map((column, index) => (
                  <th
                    key={column || `actions-${index}`}
                    className="px-6 py-2.5 text-left text-xs font-semibold text-gray-500 dark:text-gray-400"
                  >
                    {column ? t(`dashboard.${column}`) : ''}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
              {urls.map((url, index) => {
                const code = url.customCode || url.shortCode
                return (
                  <motion.tr
                    key={url.id}
                    initial={{ opacity: 0, x: -12 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.03 }}
                    className="hover:bg-gray-50 dark:hover:bg-gray-700/50"
                  >
                    <td className="max-w-md px-6 py-4">
                      <p className="truncate font-medium text-gray-900 dark:text-white">
                        {url.title || url.metadata?.title || `/${code}`}
                      </p>
                      <p className="truncate text-xs text-gray-500 dark:text-gray-400">
                        /{code} ·{' '}
                        {truncateMiddle(url.originalUrl, URL_PREVIEW_LENGTH)}
                      </p>
                    </td>
                    <td className="whitespace-nowrap px-6 py-4">
                      <div className="flex flex-col gap-1.5">
                        <span className="text-sm font-semibold tabular-nums text-gray-900 dark:text-white">
                          {url.clickCount.toLocaleString()}
                        </span>
                        {/* 목록 안에서의 상대적 크기. 0 이어도 자리는 남긴다 */}
                        <span className="block h-1 overflow-hidden rounded-sm bg-gray-100 dark:bg-gray-700">
                          <span
                            className="block h-full rounded-sm bg-primary-600"
                            style={{
                              width: `${
                                maxClicks > 0
                                  ? Math.max(
                                      2,
                                      Math.round(
                                        (url.clickCount / maxClicks) * 100
                                      )
                                    )
                                  : 0
                              }%`,
                            }}
                          />
                        </span>
                      </div>
                    </td>
                    <td className="whitespace-nowrap px-6 py-4 text-gray-500 dark:text-gray-400">
                      {formatDate(url.createdAt)}
                    </td>
                    <td className="whitespace-nowrap px-6 py-4 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Link
                          to={`/analytics/${code}`}
                          aria-label={t('dashboard.analyze')}
                          title={t('dashboard.analyzeTitle')}
                          className="inline-flex items-center gap-1 rounded-lg px-2 py-1 text-primary-600 transition hover:bg-primary-50 dark:text-primary-400 dark:hover:bg-primary-950/40"
                        >
                          <ChartBarIcon className="h-4 w-4" />
                          {t('dashboard.analyze')}
                        </Link>

                        {/*
                          원본 주소로 바로 연다. 단축 URL 로 열면 리다이렉트가
                          발생해 소유자 본인의 확인이 클릭 통계에 집계된다.
                        */}
                        <a
                          href={url.originalUrl}
                          target="_blank"
                          rel="noreferrer noopener"
                          aria-label={t('dashboard.openOriginal')}
                          title={t('dashboard.openOriginalTitle')}
                          className="rounded-lg p-1.5 text-gray-500 transition hover:bg-gray-100 hover:text-gray-700 dark:hover:bg-gray-700"
                        >
                          <ArrowTopRightOnSquareIcon className="h-4 w-4" />
                        </a>

                        <button
                          type="button"
                          onClick={() => copyToClipboard(url.shortUrl)}
                          aria-label={t('dashboard.copyShort')}
                          title={t('dashboard.copyShort')}
                          className="rounded-lg p-1.5 text-gray-500 transition hover:bg-gray-100 hover:text-gray-700 dark:hover:bg-gray-700"
                        >
                          <DocumentDuplicateIcon className="h-4 w-4" />
                        </button>

                        <button
                          type="button"
                          onClick={() => share(url)}
                          aria-label={t('dashboard.share')}
                          title={t('dashboard.share')}
                          className="rounded-lg p-1.5 text-gray-500 transition hover:bg-gray-100 hover:text-gray-700 dark:hover:bg-gray-700"
                        >
                          <ShareIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </motion.tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  )
}

/** 모달 상태 변화로 목록 전체가 다시 그려지지 않게 한다 */
export default memo(RecentUrlsTable)
